import React, { useEffect, useMemo } from 'react';
import { useAuth, useEventMemberships } from '@shared/components/AuthProvider';
import { useHistory } from '@shared/core';
import { useRouterHelper } from '@shared/core/router.helper';
import { useQueryParamHelper } from '@shared/core/queryString';
import { routePaths } from '@apps/acquisitionFunnels/createWedding/route.paths';
import { getUserScenario } from '@apps/acquisitionFunnels/createWedding/utils';
import type { CreateWeddingGuardsProps } from '@apps/acquisitionFunnels/createWedding/CreateWedding.types';

/**
 * Combined auth guard for CreateWedding routes.
 *
 * Renders `children` immediately; auth-driven redirects fire post-hydration via useEffect.
 * The SSR'd HTML is a public view safe to cache; auth-aware UI inside `children` is gated
 * separately on `isLoggedIn` at the consumer level (see CreateWeddingForm sign-out affordance,
 * SocialAccount provider sentence).
 *
 * - authMode="allowOnlyAuthenticated": redirects to /create-account if not logged in
 * - authMode="allowOnlyUnauthenticated": redirects to /createwedding if logged in
 * - authMode="requireJoyOrNonSocialAuthenticated": redirects social login users to /social-account
 */
export const CreateWeddingGuards: React.FC<CreateWeddingGuardsProps> = ({ children, authMode }) => {
  const auth = useAuth();
  const { isCurrentUserSettled, isLoggedIn } = auth;
  const needsMemberships = authMode === 'requireJoyOrNonSocialAuthenticated';

  // Skip the GQL fetch until auth has settled AND the user is known to be logged in.
  // On SSR isCurrentUserSettled is false, so the query is skipped and no per-user data
  // leaks into the cached HTML. Post-hydration the skip flips and the query fires normally.
  const { eventMemberships, loading: isLoadingEventMemberships } = useEventMemberships(undefined, {
    skip: !needsMemberships || !isCurrentUserSettled || !isLoggedIn,
  });
  const history = useHistory();
  const routeHelpers = useRouterHelper();
  const queryParamHelper = useQueryParamHelper();

  const userScenario = useMemo(() => (needsMemberships ? getUserScenario(auth, eventMemberships) : null), [needsMemberships, auth, eventMemberships]);

  const isReady = needsMemberships ? isCurrentUserSettled && !isLoadingEventMemberships : isCurrentUserSettled;

  const shouldRedirectToCreateAccount = (authMode === 'allowOnlyAuthenticated' && !isLoggedIn) || (authMode === 'requireJoyOrNonSocialAuthenticated' && !isLoggedIn);

  const shouldRedirectToCreateWedding = authMode === 'allowOnlyUnauthenticated' && isLoggedIn;

  const shouldRedirectToSocialAccount = authMode === 'requireJoyOrNonSocialAuthenticated' && isLoggedIn && userScenario === 'socialLogin';

  useEffect(() => {
    if (!isReady) {
      return;
    }

    switch (authMode) {
      case 'allowOnlyAuthenticated': {
        if (shouldRedirectToCreateAccount) {
          const path = queryParamHelper.buildRedirectPath(routeHelpers.buildPath(routePaths.createAccount.path));
          history.replace(path);
        }
        break;
      }

      case 'allowOnlyUnauthenticated': {
        if (shouldRedirectToCreateWedding) {
          const path = queryParamHelper.buildRedirectPath(routeHelpers.buildPath(routePaths.createWeddingForm.path));
          history.replace(path);
        }
        break;
      }

      case 'requireJoyOrNonSocialAuthenticated': {
        if (shouldRedirectToCreateAccount) {
          const path = queryParamHelper.buildRedirectPath(routeHelpers.buildPath(routePaths.createAccount.path));
          history.replace(path);
        } else if (shouldRedirectToSocialAccount) {
          const path = queryParamHelper.buildRedirectPath(routeHelpers.buildPath(routePaths.socialAccount.path));
          history.replace(path);
        }
        break;
      }

      default:
        break;
    }
  }, [authMode, isReady, shouldRedirectToCreateAccount, shouldRedirectToCreateWedding, shouldRedirectToSocialAccount, history, routeHelpers, queryParamHelper]);

  return <>{children}</>;
};
